// ============================================================
// characterFile - one character, or a few, as a file of their own.
//
// A session file carries the whole table and REPLACES the roster when
// it is imported. This is the other thing people want: take one
// character (or a team) out as they stand after a session, and bring
// them back into a later one beside whoever is already there.
//
// The envelope is its own kind, so the importer can tell the two apart
// and never replaces a roster with a character file.
//
// Pure. Reading re-mints every combatant id: a file id becomes a
// document id, and the same file brought in twice must be two people.
// Sheet ids are kept when they can name a document (so the Changed list
// still finds the library entry) and replaced by a stand-in when not.
// ============================================================
import { remapUnsafeIds } from '../importIds';
import { initialWeaponAmmo, mintId } from './factories';
import { nameNewInstances, type Instance } from './naming';
import { sheetArmour } from './projection';
import { combatantToFile, rosterFromFile, type CombatantFileEntry, type RosterExportOptions } from './sessionFile';
import type { Combatant } from './types';

export const CHARACTER_FILE_KIND = 'dw-characters';
const CHARACTER_FILE_VERSION = 1;

export interface CharacterFile {
  kind: typeof CHARACTER_FILE_KIND;
  version: number;
  /** When it was written, in ms. */
  exportedAt: number;
  combatants: CombatantFileEntry[];
}

/** What a character file may leave out. State off means a clean template, whatever the other two say. */
export interface CharacterExportOptions extends RosterExportOptions {
  /** false → every magazine full, whatever was fired. */
  includeAmmo: boolean;
  /** false → armour as issued, whatever wore it down. */
  includeWornArmour: boolean;
}

/** The combatant with the parts of its state the options leave out put back to fresh. */
function trimmed(c: Combatant, opts: CharacterExportOptions): Combatant {
  if (!opts.includeState) return c;
  const weaponAmmo = opts.includeAmmo ? c.state.weaponAmmo : initialWeaponAmmo(c.sheet);
  const armour = opts.includeWornArmour ? c.state.armour : sheetArmour(c);
  return { ...c, state: { ...c.state, weaponAmmo, armour } };
}

/** The file for these combatants. */
export function toCharacterFile(
  combatants: Combatant[],
  opts: CharacterExportOptions,
  now: number = Date.now(),
): CharacterFile {
  return {
    kind: CHARACTER_FILE_KIND,
    version: CHARACTER_FILE_VERSION,
    exportedAt: now,
    combatants: combatants.map((c) => combatantToFile(trimmed(c, opts), opts)),
  };
}

/** Is this parsed JSON a character file (rather than a session file, or anything else)? */
export function isCharacterFile(data: unknown): data is CharacterFile {
  const file = data as Partial<CharacterFile> | null;
  return !!file && typeof file === 'object' && file.kind === CHARACTER_FILE_KIND && Array.isArray(file.combatants);
}

/**
 * The combatants a character file holds, each under a NEW id, in file
 * order. `skipped` counts entries that could not be read at all.
 */
export function readCharacterFile(data: CharacterFile): { combatants: Combatant[]; skipped: number } {
  // A sheet id becomes a document id when the character is saved to a library: checked at the door.
  // With no sheet in the entry, the reader makes one under the ENTRY's id: both are checked.
  const safe = remapUnsafeIds(
    data.combatants,
    data.combatants.flatMap((entry) => [entry?.id, entry?.sheet?.id]),
  );
  const { combatants, skipped } = rosterFromFile(safe);
  return { combatants: combatants.map((c) => ({ ...c, id: mintId('cbt') })), skipped };
}

/** Why this file cannot be read, as a sentence; null when it can. */
export function characterFileProblem(data: CharacterFile): string | null {
  return data.version > CHARACTER_FILE_VERSION
    ? 'this character file was written by a newer version of the simulator'
    : null;
}

/**
 * The arrivals under the names the table will know them by: numbered
 * against `census` (the roster plus every waiting room) by the one rule
 * every door uses, so a second Castiel is "Castiel 2", never a twin.
 */
export function namedForTable(combatants: Combatant[], census: readonly Instance[]): Combatant[] {
  const names = nameNewInstances(
    census,
    combatants.map((c) => ({ id: c.sheet.id, name: c.name })),
  );
  return combatants.map((c, i) => (names[i] === c.name ? c : { ...c, name: names[i] }));
}

/** "castiel-2026-10-04.json", or "characters-..." for more than one. */
export function characterFileName(combatants: Combatant[], now: number = Date.now()): string {
  const day = new Date(now).toISOString().slice(0, 10);
  const one = combatants.length === 1 ? combatants[0].name : '';
  const slug = one
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `${slug || 'characters'}-${day}.json`;
}
